/* =============================================================
   bulk-discount.js — Bulk Order Discount page interactions
   • Scroll progress bar
   • IntersectionObserver reveal (.bd-reveal)
   • Pretext-inspired Canvas accordion height measurement
   • Hero stagger entrance
   • Counter animations
   • Live savings calculator
   • Savings track fill animation
   ============================================================= */
(function () {
  'use strict';

  /* ── Pretext-pattern Canvas text measurement ── */
  var _cache = new Map();

  function ptxMeasure(text, fontStr, containerWidth, lineHeightPx) {
    var key = fontStr + '|' + Math.round(containerWidth) + '|' + text.slice(0, 80);
    if (_cache.has(key)) return _cache.get(key);
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    ctx.font = fontStr;
    var words = text.split(/\s+/);
    var lines = 0; var current = '';
    for (var i = 0; i < words.length; i++) {
      var candidate = current ? current + ' ' + words[i] : words[i];
      if (ctx.measureText(candidate).width > containerWidth && current) {
        lines++; current = words[i];
      } else { current = candidate; }
    }
    if (current) lines++;
    var result = { lineCount: Math.max(1, lines), height: Math.max(1, lines) * lineHeightPx };
    _cache.set(key, result);
    return result;
  }

  function measureHeight(text, fontStr, containerWidth, lineHeightPx) {
    if (window.__bmwPretext && typeof window.__bmwPretext.measureBlockHeightCached === 'function') {
      return window.__bmwPretext.measureBlockHeightCached(text, fontStr, containerWidth, lineHeightPx);
    }
    return ptxMeasure(text, fontStr, containerWidth, lineHeightPx).height;
  }

  /* ── Progress bar ── */
  function initProgressBar() {
    var bar = document.querySelector('.bd__progress');
    if (!bar) return;
    function update() {
      var doc = document.documentElement;
      var top = doc.scrollTop || document.body.scrollTop;
      var max = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
      bar.style.width = (max > 0 ? Math.min(100, (top / max) * 100) : 0).toFixed(2) + '%';
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── Scroll reveal ── */
  function initReveal() {
    var targets = document.querySelectorAll('.bd-reveal');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ── Hero stagger ── */
  function initHeroStagger() {
    var els = document.querySelectorAll('.bd__hero [data-stagger]');
    if (!els.length) return;
    els.forEach(function (el, i) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(14px)';
      el.style.transition = 'opacity 0.5s cubic-bezier(0.16,1,0.3,1), transform 0.5s cubic-bezier(0.16,1,0.3,1)';
      el.style.transitionDelay = (i * 0.1 + 0.1) + 's';
      setTimeout(function () { el.style.opacity = '1'; el.style.transform = 'none'; }, 60);
    });
  }

  /* ── Counter animation ── */
  function animateCounter(el, target, suffix, duration) {
    if (!suffix) suffix = '';
    if (!duration) duration = 900;
    var start = performance.now();
    function step(now) {
      var p = Math.min((now - start) / duration, 1);
      var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
      el.textContent = Math.round(eased * target) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters() {
    var counters = document.querySelectorAll('[data-counter]');
    if (!counters.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        animateCounter(el, parseInt(el.getAttribute('data-counter'), 10), el.getAttribute('data-counter-suffix') || '');
        io.unobserve(el);
      });
    }, { threshold: 0.3 });
    counters.forEach(function (el) { io.observe(el); });
  }

  /* ── Savings track fill ── */
  function initSavingsTrack() {
    var fill = document.querySelector('.bd__savings-fill');
    if (!fill || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { fill.classList.add('animated'); io.unobserve(fill); }
      });
    }, { threshold: 0.5 });
    io.observe(fill);
  }

  /* ── Accordion (Pretext-measured height) ── */
  function initAccordions() {
    var btns = document.querySelectorAll('.bd__accordion-btn');
    if (!btns.length) return;
    btns.forEach(function (btn) {
      var body = btn.nextElementSibling;
      var inner = body && body.querySelector('.bd__accordion-inner');
      if (!body || !inner) return;

      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        if (expanded) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () { body.style.height = '0'; });
          btn.setAttribute('aria-expanded', 'false');
        } else {
          var text = inner.textContent.replace(/\s+/g, ' ').trim();
          var fontStr = '300 14px Inter, system-ui, sans-serif';
          var lineHeight = 25; /* 14px × 1.75 ≈ 25px */
          var containerW = inner.getBoundingClientRect().width || inner.offsetWidth || 320;
          var h = measureHeight(text, fontStr, containerW, lineHeight);
          var targetH = Math.ceil(h * 1.25) + 24; /* padding-top */
          body.style.height = targetH + 'px';
          btn.setAttribute('aria-expanded', 'true');
          body.addEventListener('transitionend', function onEnd() {
            if (btn.getAttribute('aria-expanded') === 'true') body.style.height = 'auto';
            body.removeEventListener('transitionend', onEnd);
          });
        }
      });
    });
  }

  /* ── Savings Calculator ── */
  var TIERS = [
    { min: 300, pct: 0.08, label: '300+ tires — Elite (8% off)' },
    { min: 200, pct: 0.07, label: '200+ tires — Premium (7% off)' },
    { min: 100, pct: 0.06, label: '100+ tires — Standard (6% off)' },
    { min:  50, pct: 0.05, label:  '50+ tires — Starter (5% off)' }
  ];

  function getTier(qty) {
    for (var i = 0; i < TIERS.length; i++) {
      if (qty >= TIERS[i].min) return TIERS[i];
    }
    return null;
  }

  function fmt(n) {
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function updateCalc() {
    var qtyEl   = document.getElementById('bd-calc-qty');
    var priceEl = document.getElementById('bd-calc-price');
    var result  = document.getElementById('bd-calc-result');
    if (!qtyEl || !priceEl || !result) return;

    var qty   = parseInt(qtyEl.value, 10) || 0;
    var price = parseFloat(priceEl.value) || 0;
    var empty = document.querySelector('.bd__calc-result-empty');
    var rows  = document.querySelectorAll('.bd__calc-result-row');

    if (!qty || !price) {
      if (empty) empty.style.display = '';
      rows.forEach(function (r) { r.style.display = 'none'; });
      return;
    }

    var tier = getTier(qty);
    if (!tier) {
      if (empty) {
        empty.style.display = '';
        empty.textContent = 'Minimum 50 tires required for bulk discount.';
      }
      rows.forEach(function (r) { r.style.display = 'none'; });
      return;
    }

    if (empty) empty.style.display = 'none';

    var subtotal = qty * price;
    var savings  = subtotal * tier.pct;
    var total    = subtotal - savings;

    var ids = { tier: tier.label, subtotal: fmt(subtotal), savings: fmt(savings), total: fmt(total) };
    Object.keys(ids).forEach(function (key) {
      var el = document.getElementById('bd-result-' + key);
      if (el) el.textContent = ids[key];
    });
    rows.forEach(function (r) { r.style.display = ''; });
  }

  function initCalculator() {
    var qty   = document.getElementById('bd-calc-qty');
    var price = document.getElementById('bd-calc-price');
    if (!qty || !price) return;
    qty.addEventListener('input', updateCalc);
    price.addEventListener('input', updateCalc);
    updateCalc();
  }

  /* ── Main init ── */
  function init() {
    initProgressBar();
    initReveal();
    initHeroStagger();
    initCounters();
    initSavingsTrack();
    initAccordions();
    initCalculator();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  document.addEventListener('shopify:section:load', init);

})();
